import React, { memo, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createSelector } from 'reselect';
import { message } from 'antd';
import classNames from 'classnames';
import styles from './WallpaperOpt.module.less';

import { logging } from '../../actions/log';
import { myWallpaper } from '../../constants/storageKeys';
import { PCStoreJsBridge } from '../../nativeapi';
import { updatePreviewInfo, addDownloadImg, operationImg } from '../../actions/wallpaper';
import success from '../../static/msg-success.svg';
import fail from '../../static/msg-fail.svg';

const selectLogInfo = createSelector(
  state => state.log,
  log => log.logInfo,
);
const selectWpOpteration = createSelector(
  state => state.wallpaper,
  wallpaper => wallpaper.wpOpteration,
);
const selectWpPreview = createSelector(
  state => state.wallpaper,
  wallpaper => wallpaper.wpPreviewInfo,
);

export default memo(({ wallpaper, cardInfo, logInfo }) => {
  const dispatch = useDispatch();
  const logInfoData = useSelector(selectLogInfo);
  // 处理收藏删除结果
  const wpOpteration = useSelector(selectWpOpteration);
  // 全屏预览壁纸
  const wpPreview = useSelector(selectWpPreview);
  const [isRedCollect, setIsRedCollect] = useState(false);
  const [isBusyingCollect, setIsBusyingCollect] = useState(false);
  const [isBusyingWallpaper, setIsBusyingWallpaper] = useState(false);
  const [isBusyingPreview, setIsBusyingPreview] = useState(false);
  const [isBusyingDelete, setIsBusyingDelete] = useState(false);
  const [isBusyingHidden, setIsBusyingHidden] = useState(false);

  /**
   * 初始化心
   */
  useEffect(() => {
    if (wallpaper.hasCollect !== 0) {
      setIsRedCollect(true);
    }
  }, []);

  /**
   * 操作结束处理
   * type: 1-收藏 （点赞）2-收藏列表中删除（即取消点赞）3-设为壁纸 4-从曾用壁纸中删除
   */
  useEffect(() => {
    if (
      wpOpteration &&
      wpOpteration.wallpaperId === wallpaper.wallpaperId &&
      wpOpteration.status === 0
    ) {
      // 点赞成功的处理
      if (wpOpteration.type === 1) {
        setIsRedCollect(true);
      }
      // 点赞删除、曾用中删除 成功的处理
      if (wpOpteration.type === 2 || wpOpteration.type === 4) {
        if (cardInfo.deleteCallback) {
          cardInfo.deleteCallback({
            wallpaperId: wpOpteration.wallpaperId,
            type: wpOpteration.type,
          });
        }
      }
    }
    // 点赞失败的处理
    if (
      wpOpteration.status !== 0 &&
      wpOpteration.wallpaperId === wallpaper.wallpaperId &&
      wpOpteration.type === 1
    ) {
      setIsRedCollect(false);
    }
  }, [wpOpteration]);

  const updateOptState = useCallback(() => {
    setTimeout(() => {
      setIsBusyingCollect(false);
      setIsBusyingWallpaper(false);
      setIsBusyingPreview(false);
      setIsBusyingDelete(false);
      setIsBusyingHidden(false);
    }, 300);
  }, []);

  /**
   * 点击收藏
   */
  const handleCollect = useCallback(
    wp => {
      if (isRedCollect) {
        message.error({
          content: '已赞过该壁纸',
          icon: <img className="ant-message-custom-img" src={fail} alt="success" />,
        });
        return;
      }
      if (isBusyingCollect) {
        return;
      }
      setIsRedCollect(true);
      setIsBusyingCollect(true);
      // 1-收藏 （点赞）2-收藏列表中删除（即取消点赞）3-设为壁纸 4-从曾用壁纸中删除
      if (wpOpteration && wpOpteration.load === false) {
        dispatch(operationImg({ type: 1, wallpaperId: wp.wallpaperId }));
        setIsRedCollect(true);
      }
      dispatch(
        logging({
          category: 'Wallpaper',
          action: 'C_Wallpaper_Collect',
          label: '',
          param2: {
            source: logInfo.source,
            source_p: logInfoData.source_p,
            source_pp: logInfoData.source_pp,
            wallpaperId: wallpaper.wallpaperId,
            authorId: wallpaper.author && wallpaper.author.authorId,
          },
        }),
      );
    },
    [dispatch, wpOpteration, isRedCollect, isBusyingCollect],
  );

  /**
   * 取消收藏
   */
  const handleDelete = useCallback(
    wp => {
      if (isBusyingDelete) {
        return;
      }
      setIsBusyingDelete(true);
      updateOptState();
      // 1-收藏 （点赞）2-收藏列表中删除（即取消点赞）3-设为壁纸 4-从曾用壁纸中删除
      if (wpOpteration.load === false) {
        const type = (cardInfo.isDeleteColl && 2) || (cardInfo.isDeleteUsed && 4);
        dispatch(operationImg({ type, wallpaperId: wp.wallpaperId }));
        dispatch(
          logging({
            category: 'Wallpaper',
            action: type === 2 ? `C_Wallpaper_Delete_Collect` : 'C_Wallpaper_Delete_Used',
            label: '',
            param2: {
              source: logInfo.source,
              source_p: logInfoData.source_p,
              source_pp: logInfoData.source_pp,
              wallpaperId: wallpaper.wallpaperId,
              authorId: wallpaper.author && wallpaper.author.authorId,
            },
          }),
        );
      }
    },
    [dispatch, wpOpteration, isBusyingDelete],
  );

  const setWallpaper = useCallback(
    param => {
      // 直接设置壁纸
      PCStoreJsBridge.wpSetWallpaper(param).then(ret2 => {
        if (ret2.succ === 'true') {
          message.success({
            content: '壁纸设置成功，请查看电脑桌面',
            className: wpPreview && wpPreview.isShow ? 'ant-message-notice-center' : '',
            icon: <img className="ant-message-custom-img" src={success} alt="success" />,
          });
          window.wpSettingWallpaperTaskList = window.wpSettingWallpaperTaskList.filter(
            item => param.id !== item.id,
          );
        } else {
          message.error({
            content: '设置失败，请稍后再试',
            icon: <img className="ant-message-custom-img" src={fail} alt="success" />,
          });
        }
      });
    },
    [wpPreview],
  );

  const testUrl = useCallback(wp => {
    if (!wp || !wp.wallpaperImg || !wp.wallpaperImg[2] || !wp.wallpaperImg[2].uri) {
      message.error({
        content: '壁纸不存在',
        icon: <img className="ant-message-custom-img" src={fail} alt="success" />,
      });
      return false;
    }
    return true;
  }, []);

  const handleSetWallpaper = useCallback(
    wp => {
      if (isBusyingWallpaper || !testUrl(wp)) {
        return;
      }
      setIsBusyingWallpaper(true);
      updateOptState();
      // 1-收藏 （点赞）2-收藏列表中删除（即取消点赞）3-设为壁纸 4-从曾用壁纸中删除
      if (wpOpteration && wpOpteration.load === false) {
        dispatch(operationImg({ type: 3, wallpaperId: wp.wallpaperId }));
      }
      const param = { url: wp.wallpaperImg[2].uri, id: wp.wallpaperId };
      // 判断是否下载图片
      PCStoreJsBridge.wpIsDownloadedImg(param).then(ret => {
        if (ret.succ === 'true') {
          // 同时正在设置壁纸中的任务控制在三个以内
          if (!window.wpSettingWallpaperTaskList) {
            window.wpSettingWallpaperTaskList = [param];
            setWallpaper(param);
          } else if (
            window.wpSettingWallpaperTaskList &&
            window.wpSettingWallpaperTaskList.length < 3
          ) {
            const found = window.wpSettingWallpaperTaskList.find(wp => wp.id === param.id);
            if (!found) {
              window.wpSettingWallpaperTaskList.push(param);
              setWallpaper(param);
            }
          } else {
            message.error({
              content: '设置壁纸太频繁，请稍后再试',
              icon: <img className="ant-message-custom-img" src={fail} alt="success" />,
            });
          }
        } else {
          // 请求下载图片
          PCStoreJsBridge.wpDownloadImg({
            id: wp.wallpaperId,
            url: wp.wallpaperImg[2].uri,
            type: 'wallpaper',
          }).then(ret2 => {
            if (ret2.succ === 'true') {
              dispatch(addDownloadImg({ wallpaper: { ...wp, type: 'wallpaper' } }));
            } else {
              message.error({
                content: '壁纸下载失败，请稍后再试',
                icon: <img className="ant-message-custom-img" src={fail} alt="success" />,
              });
            }
          });
        }
      });

      dispatch(
        logging({
          category: 'Wallpaper',
          action: 'C_Wallpaper_Setting',
          label: '',
          param2: {
            source: logInfo.source,
            source_p: logInfoData.source_p,
            source_pp: logInfoData.source_pp,
            wallpaperId: wp.wallpaperId,
            authorId: wallpaper.author && wallpaper.author.authorId,
          },
        }),
      );
    },
    [wpOpteration, isBusyingWallpaper],
  );

  const handleSetPreview = useCallback(
    wp => {
      if (isBusyingPreview || !testUrl(wp)) {
        return;
      }
      setIsBusyingPreview(true);
      updateOptState();
      // 判断是否下载图片
      PCStoreJsBridge.wpIsDownloadedImg({ url: wp.wallpaperImg[2].uri, id: wp.wallpaperId }).then(
        ret => {
          if (ret.succ === 'true') {
            // 设置窗口大小，并且显示预览的图片
            PCStoreJsBridge.wpMaximizeStroe().then(ret2 => {
              if (ret2.succ) {
                dispatch(
                  updatePreviewInfo({
                    isShow: true,
                    url: ret.msg,
                    wallpaper: wp,
                  }),
                );
              } else {
                message.error({
                  content: '预览失败，请稍后再试',
                  icon: <img className="ant-message-custom-img" src={fail} alt="success" />,
                });
              }
            });
          } else {
            // 请求下载图片
            PCStoreJsBridge.wpDownloadImg({
              id: wp.wallpaperId,
              url: wp.wallpaperImg[2].uri,
              type: 'preview',
            }).then(ret2 => {
              if (ret2.succ === 'true') {
                dispatch(addDownloadImg({ wallpaper: { ...wp, type: 'preview' } }));
              } else {
                message.error({
                  content: '壁纸下载失败，请稍后再试',
                  icon: <img className="ant-message-custom-img" src={fail} alt="success" />,
                });
              }
            });
          }
        },
      );
      dispatch(
        logging({
          category: 'Wallpaper',
          action: 'C_Wallpaper_Preview',
          label: '',
          param2: {
            source: logInfo.source,
            source_p: logInfoData.source_p,
            source_pp: logInfoData.source_pp,
            wallpaperId: wallpaper.wallpaperId,
            authorId: wallpaper.author && wallpaper.author.authorId,
          },
        }),
      );
    },
    [dispatch, isBusyingPreview],
  );

  const handleWpPreviewHidden = useCallback(() => {
    if (isBusyingHidden) {
      return;
    }
    setIsBusyingHidden(true);
    updateOptState();
    PCStoreJsBridge.wpExitMaximizeStroe().then(ret => {
      if (ret.succ) {
        dispatch(updatePreviewInfo(null));
      }
    });

    dispatch(
      logging({
        category: 'Wallpaper',
        action: 'C_Wallpaper_Preview_Out',
        label: '',
        param2: {
          source: logInfo.source,
          source_p: logInfoData.source_p,
          source_pp: logInfoData.source_pp,
          wallpaperId: wallpaper.wallpaperId,
          authorId: wallpaper.author && wallpaper.author.authorId,
        },
      }),
    );
  }, [dispatch, isBusyingHidden]);

  return (
    <div className={cardInfo.isBig ? styles.wpopt_big : styles.wpopt}>
      {cardInfo.isCollect && (
        <div
          className={classNames('box box1', { isRedCollect })}
          onClick={e => {
            e.stopPropagation();
            handleCollect(wallpaper);
          }}
          title=" 收藏 "
        >
          <div className="icon default" />
          <div className="icon hover" />
          <div className="heart" />
          <div className="circle" />
        </div>
      )}
      {cardInfo.isWallpaper && (
        <div
          className="opt_wallpaper"
          onClick={e => {
            e.stopPropagation();
            handleSetWallpaper(wallpaper);
          }}
          title=" 设为壁纸 "
        />
      )}
      {(cardInfo.isDeleteColl || cardInfo.isDeleteUsed) && (
        <div
          className="opt_delete"
          onClick={e => {
            e.stopPropagation();
            handleDelete(wallpaper);
          }}
          title=" 删除 "
        />
      )}
      {cardInfo.isPreview && (
        <div
          className="opt_preview"
          onClick={e => {
            e.stopPropagation();
            handleSetPreview(wallpaper);
          }}
          title=" 预览 "
        />
      )}
      {cardInfo.isPreviewOut && (
        <div
          className="opt_previeweout"
          onClick={e => {
            e.stopPropagation();
            handleWpPreviewHidden(wallpaper);
          }}
          title=" 收起 "
        />
      )}
    </div>
  );
});
